import { LocationMarkerIcon } from "@heroicons/react/outline";
import { stringifyUrl } from "query-string";
import Tooltip from "rc-tooltip";
import React, { useCallback, useMemo } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { PLACEMENT_DISTANCE_LIMIT } from "../../../components/Dashboard";
import ExternalLink from "../../../components/ExternalLink";
import InfoIcon from "../../../components/icons/InfoIcon";
import InfoItems, { InfoItem } from "../../../components/InfoItems";
import StatusDot from "../../../components/StatusDot";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useLocations } from "../../../context/locations";
import { avatarsApi } from "../../../utils/constants";
import { formatDateToLocale } from "../../../utils/dates";
import {
  formatHotspotName,
  formatHotspotPlace
} from "../../../utils/hotspotUtils";
import { pointDistanceMemoized } from "../../../utils/memoizedMethods";
import { truncateText } from "../../../utils/text";
import { Install } from "../../../utils/types";
import useMapConfig from "../../../utils/useMapConfig";
import MemberCell from "../../Workspace/common/MemberCell";
import { isActiveInstall } from "../../Workspace/InstallsData/utils";

const InstallInfoTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { installs } = useInstalls();
  const { locations } = useLocations();
  const { hotspotsMap } = useHotspots();
  const renderDistance = useMapConfig((state) => state.renderDistance);

  const currentInstall = useMemo(
    () => (props.match.params.id ? installs[props.match.params.id] : undefined),
    [installs, props.match.params.id]
  );

  const currentHotspot = useMemo(
    () =>
      currentInstall ? hotspotsMap[currentInstall.hotspot_uuid] : undefined,
    [currentInstall, hotspotsMap]
  );

  const currentLocation = useMemo(
    () =>
      currentInstall ? locations[currentInstall.location_uuid] : undefined,
    [currentInstall, locations]
  );

  const getInfoItems = useCallback(
    (install: Install) => {
      const {
        created_at,
        updated_at,
        created_by,
        updated_by,
        installed_at,
        uninstalled_at
      } = install;

      const infoItems: InfoItem[] = [
        {
          label: "Hotspot",
          value: (
            <Link
              className="relative flex items-center space-x-3 group text-sm font-medium text-gray-900 dark:text-white"
              to={`/hotspots/${currentInstall?.hotspot_uuid}/installs`}
            >
              <div className="flex-shrink-0">
                <span className="inline-block relative">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={stringifyUrl({
                      url: `${avatarsApi}/bottts/${currentInstall?.hotspot_uuid}.svg`,
                      query: { w: 40, h: 40 }
                    })}
                    alt="Hotspot avatar"
                  />
                  {currentHotspot && (
                    <StatusDot
                      hotspot={currentHotspot}
                      withoutCustomRewardScaling
                      className="absolute top-0 right-0 block"
                    />
                  )}
                </span>
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {currentHotspot
                    ? formatHotspotName(currentHotspot)
                    : truncateText(currentInstall?.hotspot_uuid ?? "", 20)}
                </p>
                <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                  {formatHotspotPlace(currentHotspot?.place)}
                </p>
              </div>
            </Link>
          )
        },
        {
          label: "Location",
          value: (
            <Link
              className="relative flex items-center group"
              to={`/locations/${currentLocation?.uuid}`}
            >
              <div className="flex-shrink-0 flex justify-center items-center w-10">
                <LocationMarkerIcon className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {currentLocation?.name}
                </p>
                <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                  {formatHotspotPlace(currentLocation?.address)}
                </p>
              </div>
            </Link>
          )
        },
        {
          label: "Installed",
          value: installed_at ? formatDateToLocale(installed_at) : null
        },
        {
          label: "Uninstalled",
          value: uninstalled_at ? formatDateToLocale(uninstalled_at) : null
        },
        {
          label: "Added",
          value: (
            <MemberCell
              memberUuid={created_by}
              subtitle={formatDateToLocale(created_at)}
            />
          )
        },
        {
          label: "Updated",
          value: updated_by ? (
            <MemberCell
              memberUuid={updated_by}
              subtitle={formatDateToLocale(updated_at)}
            />
          ) : null
        }
      ];

      if (isActiveInstall(install)) {
        infoItems.push({
          label: (
            <>
              Placement distance
              <Tooltip
                overlay={
                  <>
                    Distance between the placement (hotspot location assertion)
                    and the physical location. Placements over{" "}
                    {renderDistance(PLACEMENT_DISTANCE_LIMIT, false)} away from
                    the physical install are not recommended as{" "}
                    <ExternalLink
                      className="inline-flex text-xs"
                      text="PoCv11"
                      url="https://blog.helium.com/pocv11-explained-call-to-action-4add36c75a1d"
                    />{" "}
                    will invalidate witnesses resulting in less rewards.
                  </>
                }
              >
                <InfoIcon className="ml-1 inline-flex h-4 w-4" />
              </Tooltip>
            </>
          ),
          value:
            currentHotspot?.lng && currentLocation
              ? renderDistance(
                  pointDistanceMemoized(
                    [currentHotspot.lng, currentHotspot.lat],
                    [currentLocation.lng, currentLocation.lat]
                  ),
                  false
                )
              : null
        });
      }

      return infoItems;
    },

    [
      currentHotspot,
      currentInstall?.hotspot_uuid,
      currentLocation,
      renderDistance
    ]
  );

  return (
    <InfoItems infoItems={currentInstall ? getInfoItems(currentInstall) : []} />
  );
};

export default InstallInfoTab;
